import { ReactElement, useMemo } from 'react';
import { ApexOptions } from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import { ScatterSplit, pointsInBox } from './auxSensorChannels';
import {
  AUX_COLORS,
  AuxPlotSeriesSpec,
  RATIO_ONE_ANNOTATION,
  auxBaseOptions,
  lineSpec,
  ratioYAxis,
  scatterSpec,
  styledApexSeries,
  xAxisLabel,
} from './auxPlotStyle';
import AuxFitControls, { AuxFitFormat } from './AuxFitControls';
import AuxPlotCard from './AuxPlotCard';
import { useScatterFit } from './useScatterFit';

/** The x-axis quantity one slip-ratio scatter plots against, and its fit-window slider. */
export interface RatioScatterAxis {
  /** Axis title, units included. */
  title: string;
  /** Unit for the tooltip and the fit-window readout. */
  unit: string;
  tooltipDigits: number;
  /** The two-thumb fit-window slider, running from `min` (0 by default) to `max`. */
  fitWindow: {
    label: string;
    min?: number;
    max: number;
    step?: number;
    /** Decimals in the slider readout. */
    digits?: number;
    /** The window before the user has moved it; the full range when omitted. */
    initial?: { low: number; high: number };
  };
  format: AuxFitFormat;
}

interface AuxRatioScatterCardProps {
  title: string;
  /** `useScatterFit` storage key — one per plot, so each keeps its own fit settings. */
  storageKey: string;
  /** Samples passing the min-speed threshold (`included`) and those it masks (`excluded`). */
  scatter: ScatterSplit;
  axis: RatioScatterAxis;
  threshold: number;
  pairLabel: string;
  emptyMessage: string;
  isLoading: boolean;
}

const CHART_HEIGHT = 380;

/**
 * The slip-ratio scatter card shared by the force and acceleration plots. Y is the
 * slip ratio (wheel speed 1 / wheel speed 2) and X is whatever `axis` describes.
 * Samples masked by the shared min-speed threshold are drawn light grey, and a
 * dashed line marks ratio = 1 (agreement).
 *
 * A two-thumb x-window slider is always on screen; samples outside the window are
 * drawn mid grey. "Show fit" adds a least-squares fit of ratio on x over the
 * samples inside it.
 * "Zoom to window" frames both axes on the selected points — those passing every
 * mask, and the fit window when the fit is shown — 10% larger than their extent,
 * and leaves out whatever falls outside that box.
 */
export default function AuxRatioScatterCard({
  title,
  storageKey,
  scatter: { included: points, excluded: masked },
  axis,
  threshold,
  pairLabel,
  emptyMessage,
  isLoading,
}: AuxRatioScatterCardProps): ReactElement {
  const seriesPoints = useMemo(() => [points], [points]);
  const { fitWindow } = axis;
  const scatterFit = useScatterFit(seriesPoints, fitWindow.max, storageKey, {
    defaultWindow: fitWindow.initial,
    minX: fitWindow.min,
    windowAlwaysOn: true,
  });
  const { included, excluded, fitLine, fit } = scatterFit.results[0];
  const { zoom } = scatterFit;

  const specs = useMemo<AuxPlotSeriesSpec[]>(() => {
    const out = [scatterSpec(pairLabel, included, AUX_COLORS.ratio)];
    const outside = pointsInBox(excluded, zoom);
    if (outside.length) out.push(scatterSpec('Outside fit window', outside, AUX_COLORS.outsideFit));
    const low = pointsInBox(masked, zoom);
    if (low.length) out.push(scatterSpec(`Below ${threshold.toFixed(1)} m/s`, low, AUX_COLORS.masked));
    if (fitLine) out.push(lineSpec('Fit', fitLine, AUX_COLORS.fit));
    return out;
  }, [included, excluded, masked, threshold, fitLine, pairLabel, zoom]);

  const { series: chartSeries, options: styleOptions } = useMemo(() => styledApexSeries(specs), [specs]);

  const options = useMemo<ApexOptions>(() => {
    const base = auxBaseOptions(CHART_HEIGHT);
    const yAxis = ratioYAxis(pairLabel);
    const showRatioOne = !zoom || (zoom.yMin <= 1 && zoom.yMax >= 1);
    return {
      ...base,
      ...styleOptions,
      chart: { ...base.chart, type: 'line' },
      legend: { ...base.legend, show: specs.length > 1 },
      xaxis: {
        type: 'numeric',
        tickAmount: 8,
        // Always present, even when undefined: Apex merges option updates into the
        // previous ones, so leaving the keys out would keep the old zoom range.
        min: zoom?.xMin,
        max: zoom?.xMax,
        title: { text: axis.title, style: { fontSize: '13px', fontWeight: 600 } },
        // Same zoom-following precision as the time card's x-axis, so the
        // slip-ratio plots do not print a tick row one of them cannot resolve.
        labels: { formatter: xAxisLabel() },
      },
      yaxis: zoom ? { ...yAxis, min: zoom.yMin, max: zoom.yMax } : yAxis,
      annotations: showRatioOne ? RATIO_ONE_ANNOTATION : {},
      tooltip: {
        shared: false,
        intersect: true,
        x: { formatter: (value: number) => `${value.toFixed(axis.tooltipDigits)} ${axis.unit}` },
        y: { formatter: (value: number) => (value != null ? value.toFixed(3) : '') },
      },
    };
  }, [styleOptions, specs.length, pairLabel, zoom, axis.title, axis.unit, axis.tooltipDigits]);

  return (
    <AuxPlotCard
      title={title}
      isLoading={isLoading}
      isEmpty={points.length === 0 && masked.length === 0}
      emptyMessage={emptyMessage}
      controls={!isLoading && points.length > 0 ? (
        <AuxFitControls
          showFit={scatterFit.showFit}
          onToggleFit={scatterFit.setShowFit}
          window={scatterFit.window}
          onWindowChange={scatterFit.setWindow}
          sliderLabel={fitWindow.label}
          sliderUnit={axis.unit}
          sliderMin={fitWindow.min}
          sliderMax={fitWindow.max}
          sliderStep={fitWindow.step}
          sliderDigits={fitWindow.digits}
          alwaysShowWindow
          readouts={[{ fit }]}
          zoom={{ enabled: scatterFit.zoomToWindow, onToggle: scatterFit.setZoomToWindow }}
          format={axis.format}
        />
      ) : null}
    >
      <ReactApexChart options={options} series={chartSeries} type="line" height={CHART_HEIGHT} />
    </AuxPlotCard>
  );
}
